import { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import type { ComponentProps } from 'react';
import { BoardCard } from './BoardCard';
import { cardDragId } from './drag-ids';

type SortableBoardCardProps = ComponentProps<typeof BoardCard>;

function SortableBoardCardImpl(props: SortableBoardCardProps) {
  const { card, columnId } = props;
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: cardDragId(card._id),
    data: { type: 'card', columnId },
    // Touch scrolling must not accidentally pick up a project. Compact mode
    // keeps the accessible Move to menu rather than relying on cross-lane drag.
    disabled: { draggable: compact, droppable: false },
  });

  return (
    <Box
      ref={setNodeRef}
      data-testid={`board-card-${card._id}`}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        // Hide entirely when being dragged — DragOverlay renders the
        // floating preview that follows the cursor.
        opacity: isDragging ? 0 : 1,
      }}
      {...attributes}
      {...listeners}
    >
      <BoardCard {...props} />
    </Box>
  );
}

export const SortableBoardCard = memo(SortableBoardCardImpl);
